import { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'

import { Button } from '@gto/ui/button'
import { IconButton } from '@gto/ui/icon-button'
import { ChevronLeftIcon, ChevronRightIcon } from '@gto/ui/icons/outline'
import { Skeleton } from '@gto/ui/skeleton'
import { Typography } from '@gto/ui/typography'

import { useRoomUnverifiedReportsList } from '@/api/gen'
import { useAuth } from '@/hooks/use-auth'
import { ReportUsernameModal } from '@/pages/deals/components/report-username-modal'

import { StatsBoxWrapper } from './stats-box-wrapper'
import { UnverifiedAccountCard } from './unverified-account-card'

export const UnverifiedAccounts = () => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const { isAuthorized, isInitialized } = useAuth()

  const [showModal, setShowModal] = useState(false)

  const onToggleReportModal = () => {
    setShowModal((prev) => !prev)
  }

  const { data = [], isFetching } = useRoomUnverifiedReportsList({
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      placeholderData: keepPreviousData,
      enabled: isAuthorized,
    },
  })

  const containerRef = useRef<HTMLDivElement>(null)

  const onNext = () => {
    setCurrentIndex((prev) => (prev + 1 >= data.length ? 0 : prev + 1))
  }
  const onPrev = () => {
    setCurrentIndex((prev) => (prev - 1 < 0 ? data.length - 1 : prev - 1))
  }

  const controls = !isFetching && isInitialized && data.length > 1 && (
    <div className="flex gap-1">
      <IconButton
        edge="start"
        onClick={onPrev}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        edge="end"
        onClick={onNext}
      >
        <ChevronRightIcon />
      </IconButton>
    </div>
  )

  return (
    <StatsBoxWrapper
      className="max-w-[33%] flex-[1_1_0px] md:max-w-full md:flex-[1_0_auto]"
      controls={controls}
      title={<FormattedMessage id="rewards.stats.unverifiedAccounts.title" />}
    >
      <div className="flex h-full flex-col gap-6">
        <div className="grow overflow-hidden">
          {isFetching || !isInitialized ? (
            <>
              <Skeleton
                className="mb-1"
                variant="rectangular"
                width="100%"
              />
              <Skeleton
                variant="rectangular"
                width="80%"
              />
            </>
          ) : data.length === 0 ? (
            <Typography
              color="text.secondary"
              variant="body2"
            >
              <FormattedMessage id="rewards.stats.noUnverified" />
            </Typography>
          ) : (
            <div
              ref={containerRef}
              className="flex w-full transition-transform duration-300 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * 100}%)`,
              }}
            >
              {data.map((report, index) => (
                <div
                  // eslint-disable-next-line @eslint-react/no-array-index-key -- server-ordered report list; slide cards are stateless; carousel index lives on the container
                  key={index}
                  className="w-full flex-[0_0_100%] px-2"
                >
                  <UnverifiedAccountCard report={report} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <Button
            color="secondary"
            size="small"
            variant="contained"
            onClick={onToggleReportModal}
          >
            <FormattedMessage id="common.controls.reportNewAccount" />
          </Button>
        </div>
        <ReportUsernameModal
          open={showModal}
          onClose={onToggleReportModal}
        />
      </div>
    </StatsBoxWrapper>
  )
}
